import {
  addDays,
  addMonths,
  addWeeks,
  compareAsc,
  endOfWeek,
  format,
  getMonth,
  getQuarter,
  getYear,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import i18next from 'i18next';
import type { ItemV2 } from '@/lib/api/types/items';
import type {
  DepictionDateHeaderOptions_v1 as DateHeaderOptions,
  IDateHeaderSelector,
  ISwimlaneHeaderProps,
  RMFiscalPeriodType as PeriodType,
} from '@/lib/api/types/loop-visualization';

const EMPTY_DATE_HEADER_ID = 'no_date';
const PERIOD_SIZES = {
  year: 12,
  half: 6,
  quarter: 3,
  month: 1,
  week: 1,
} as const;

/**
 * Generate date headers for Swimlane based on items dates
 * @param headerSelector
 * @param items
 * @param includeNullHeader
 * @returns Array of ISwimlaneHeaderProps
 */
export function generateDateHeaders(
  headerSelector: IDateHeaderSelector,
  items: ItemV2[],
  includeNullHeader: boolean = false,
): ISwimlaneHeaderProps[] {
  const headerDates = getHeaderDates(headerSelector, items);
  const hasNullDate = includeNullHeader || headerDates.includes(null);
  const validDates = headerDates.filter(Boolean) as string[];
  const sortedDates = validDates.sort(compareAsc);
  const today = format(new Date(), 'yyyy-MM-dd');
  const earliestDate = sortedDates[0] || today;
  const latestDate = sortedDates[sortedDates.length - 1] || today;
  const periodType = getDateHeaderPeriod(headerSelector.header);
  const headerStartPeriodDate = getPeriodStartDate(earliestDate, periodType);
  const headerEndPeriodDate = getPeriodStartDate(latestDate, periodType);
  const dateHeaderPeriods = getDatesSpanningPeriod(
    headerStartPeriodDate,
    headerEndPeriodDate,
    periodType,
  );
  const headers = dateHeaderPeriods.map((period) => {
    return {
      label: getDateHeaderLabel(periodType, period),
      id: period,
      selector: headerSelector,
      headerColor: '#1c1c1d',
      isDateHeader: true,
    };
  });

  if (hasNullDate) {
    headers.push({
      label: i18next.t(($) => $.loopVisualization.layout.headers.fields.noDate),
      id: EMPTY_DATE_HEADER_ID,
      selector: headerSelector,
      headerColor: '#1c1c1d',
      isDateHeader: true,
    });
  }
  return headers;
}

/**
 * Returns the period of the date header
 * @param header
 * @returns The period (week, month, quarter, half, year)
 */
export function getDateHeaderPeriod(header: DateHeaderOptions): PeriodType {
  if (header.startsWith('startDate')) {
    return header.slice(10) as PeriodType;
  }

  return header.slice(8) as PeriodType;
}

/**
 * Returns the set relevant dates for the given date header and item schedules
 * @param header
 * @param items
 * @returns Array of date strings or nulls
 */
export function getHeaderDates(header: IDateHeaderSelector, items: ItemV2[]): (string | null)[] {
  if (!header || !items) return [];

  let dates: (string | null | undefined)[] = [];
  if (header.type === 'DateSelector' && header.header.startsWith('startDate')) {
    dates = items.map((item) => item.plannedStartDateTime);
  } else {
    dates = items.map((item) => item.plannedFinishDateTime);
  }

  // de-dupe
  const validDates = [...new Set(dates)];
  const datesForHeaders = validDates.map((date) => {
    if (!date) return null;
    return date.substring(0, 10);
  });

  return datesForHeaders;
}

/**
 * Returns start date of the period containing the passed date
 * @param date
 * @param periodType
 * @returns Start date of period as string
 */
function getPeriodStartDate(date: string, periodType: PeriodType): string {
  const d = parseISO(date);
  if (periodType === 'half') {
    const quarter = getQuarter(d);
    if (quarter === 1 || quarter === 2) {
      return format(startOfYear(d), 'yyyy-MM-dd');
    } else {
      return format(new Date(getYear(d), 6, 1), 'yyyy-MM-dd');
    }
  }

  const startFns: Record<Exclude<PeriodType, 'half'>, (d: Date) => Date> = {
    year: startOfYear,
    quarter: startOfQuarter,
    month: startOfMonth,
    week: (d) => startOfWeek(d, { weekStartsOn: 0 }),
  };

  return format(startFns[periodType as Exclude<PeriodType, 'half'>](d), 'yyyy-MM-dd');
}

/**
 * Returns an array of dates representing the start of each period spanning from fromDate to toDate
 * @param fromDate
 * @param toDate
 * @param periodType
 * @returns Array of date strings
 */
function getDatesSpanningPeriod(
  fromDate: string,
  toDate: string,
  periodType: PeriodType,
): string[] {
  const isWeek = periodType === 'week';
  const stepSize = PERIOD_SIZES[periodType];

  const startDate = parseISO(fromDate);
  const endDate = addDays(parseISO(toDate), 1); // add one so that the span's endDate is inclusive

  const addInterval = (date: Date, amount: number): Date =>
    isWeek ? addWeeks(date, amount) : addMonths(date, amount);

  const spanDates: string[] = [];
  for (let i = 0; addInterval(startDate, i * stepSize).getTime() < endDate.getTime(); i++) {
    spanDates.push(format(addInterval(startDate, i * stepSize), 'yyyy-MM-dd'));
  }
  return spanDates;
}

function getDateHeaderLabel(periodType: PeriodType, date: string | null): string {
  if (!date) return '';
  const d = parseISO(date);

  switch (periodType) {
    case 'week': {
      const weekStart = startOfWeek(d, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(d, { weekStartsOn: 0 });
      if (getMonth(weekStart) === getMonth(weekEnd)) {
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'd')}`;
      }
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`;
    }
    case 'month':
      return format(d, 'MMMM yyyy');
    case 'quarter': {
      const quarter = getQuarter(d);
      return `Q${quarter} ${format(d, 'yyyy')}`;
    }
    case 'half': {
      const half = getMonth(d) < 6 ? 'H1' : 'H2';
      return `${half} ${format(d, 'yyyy')}`;
    }
    case 'year':
      return format(d, 'yyyy');
  }
}

/**
 * Returns the date header ID for an item based on its start and end dates
 * @param headerSelector
 * @param startDate
 * @param endDate
 * @returns header ID as string
 */
export function getItemDateHeaderID(
  headerSelector: IDateHeaderSelector,
  startDate: string | null,
  endDate: string | null,
): string {
  if (!startDate && !endDate) {
    return EMPTY_DATE_HEADER_ID;
  }

  const periodType = getDateHeaderPeriod(headerSelector.header);
  if (headerSelector.header.startsWith('startDate')) {
    return startDate
      ? getPeriodStartDate(startDate.substring(0, 10), periodType)
      : EMPTY_DATE_HEADER_ID;
  }
  return endDate ? getPeriodStartDate(endDate.substring(0, 10), periodType) : EMPTY_DATE_HEADER_ID;
}
